import { useContext, useEffect, useState } from "react";
import { AlertComponent } from "../../../components";
import { useAppDesignPdf, useRequest } from "../../../hooks";
import { ClientProps, WellProps } from "../../../interfaces/interfaces";
import { DocRow, DocumentsPortalLayout } from "../../../components/documents/DocumentsPortalLayout";
import DataContext from "../../../context/DataContext";
import { useNavigate } from "react-router-dom";

const isSuccessful = (r: any) => r?.success || r?.succes;
const getRows = (r: any) =>
  Array.isArray(r?.data) ? r.data : r?.data?.rows ?? [];

const latestUploadByTallyId = (rows: DocRow[]) => {
  const map = new Map<number, DocRow>();
  rows.forEach((row) => {
    if (!row.tallyId) return;
    const cur = map.get(row.tallyId);
    if (!cur || new Date(row.date).getTime() > new Date(cur.date).getTime())
      map.set(row.tallyId, row);
  });
  return map;
};

export const TallyDocAdmin = () => {
  const navigate = useNavigate();
  const { handleRequest } = useRequest();
  const { downloadTallyPdf } = useAppDesignPdf();
  const { clientOptions, fetchDataClient } = useContext(DataContext);

  const [wells, setWells] = useState<WellProps[]>([]);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [wellsLoading, setWellsLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);

  useEffect(() => { fetchDataClient(); }, []);
  useEffect(() => { setTimeout(() => setIsError(false), 4000); }, [isError]);

  const handleClientChange = (client: ClientProps | null) => {
    setSelectedWellId(null);
    if (!client) { setWells([]); setRows([]); return; }
    const opts: RequestInit = { method: "GET" };
    setWellsLoading(true);
    setRowsLoading(true);
    Promise.all([
      handleRequest({ endpoint: `well/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `tally-list/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `tally-detail?client=${client.id}`, options: opts }),
    ])
      .then(([wellsRes, uploadedRes, appRes]) => {
        const wellData: WellProps[] = isSuccessful(wellsRes) ? wellsRes.data : [];
        setWells(wellData);
        setWellsLoading(false);
        const wellMap = new Map<number, string>(wellData.map((w) => [w.id as number, w.name]));
        const uploaded: DocRow[] = (isSuccessful(uploadedRes) ? uploadedRes.data : []).map(
          (item: any) => ({
            rowId: `u-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellId: item.idWell,
            wellName: wellMap.get(item.idWell) ?? "",
            source: "uploaded" as const,
            path: item.path,
            owner: item.user?.name ?? "—",
            tallyId: item.tallyId,
          }),
        );
        const uploadsByTally = latestUploadByTallyId(uploaded);
        const app: DocRow[] = getRows(appRes).map((item: any) => {
          const def = uploadsByTally.get(item.id);
          return {
            rowId: `t-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellId: item.well?.id,
            wellName: item.well?.name ?? "",
            source: "app" as const,
            appId: item.id,
            owner: item.createdBy ?? "—",
            defaultUploadPath: def?.path,
            defaultUploadName: def?.customName,
          };
        });
        const sorted = [...uploaded, ...app].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setRows(sorted);
        setRowsLoading(false);
      })
      .catch(() => { setIsError(true); setWellsLoading(false); setRowsLoading(false); });
  };

  const fetchTallyByKey = (key: string, name: string) => {
    const opts: RequestInit = {
      method: "POST",
      body: JSON.stringify({ key }),
      headers: { responseType: "Buffer" } as any,
    };
    handleRequest({ endpoint: "pdf-tally-adm", options: opts })
      .then((res) => {
        if (!res.success) { setIsError(true); return; }
        const url = window.URL.createObjectURL(
          new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name.endsWith(".pdf") ? name : `${name}.pdf`);
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
      })
      .catch(() => setIsError(true));
  };

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.source === "uploaded" && row.path) {
      fetchTallyByKey(row.path, row.customName);
    } else if (row.source === "app") {
      if (row.defaultUploadPath) {
        fetchTallyByKey(row.defaultUploadPath, row.defaultUploadName ?? row.customName);
      } else if (row.appId != null) {
        await downloadTallyPdf(row.appId, row.customName);
      }
    }
  };

  return (
    <>
      <DocumentsPortalLayout
        title="Tally Designs"
        subtitle="Browse, filter and download tally designs"
        wells={wells}
        wellsLoading={wellsLoading}
        rows={rows}
        rowsLoading={rowsLoading}
        selectedWellId={selectedWellId}
        onWellSelect={setSelectedWellId}
        onDownload={handleDownload}
        noWellMessage="Select a client to load their tally designs"
        isAdmin
        clientOptions={clientOptions}
        onClientChange={handleClientChange}
        onEdit={(row) => navigate(`/tally/${row.appId}`)}
        isError={isError}
      />
      {isError && <AlertComponent type="error" />}
    </>
  );
};
